import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'
import toast from 'react-hot-toast'

import {
  useGetShop,
  getGetShopQueryKey,
} from '@/api/generated/v2-shop/v2-shop'
import {
  useGetScriptTagStatus,
  useInstallScriptTag,
  useUninstallScriptTag,
  getGetScriptTagStatusQueryKey,
} from '@/api/generated/v2-scripttag/v2-scripttag'
import type {
  ShopResponse as OrvalShopResponse,
  ScriptTagStatusResponse,
} from '@/api/schemas'
import { SetupPage } from '@/features/setup/SetupPage'
import { GuideStep } from '@/components/guide/GuideStep'
import { TipBox } from '@/components/guide/TipBox'
import { Button } from '@/components/ui/Button'
import { auth } from '@/lib/auth'
import { useConfirm } from '@/hooks/useConfirm'

export const Route = createFileRoute('/_dashboard/setup/script')({
  component: ScriptSetupPage,
})

function ScriptSetupPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()

  // --- Orval Query 훅: Shop 정보 조회 ---
  const { data: shopRaw, isLoading: shopLoading } = useGetShop({
    query: { enabled: auth.isAuthenticated() },
  })
  const shop = (shopRaw ?? null) as OrvalShopResponse | null

  // --- Orval Query 훅: 스크립트 설치 상태 ---
  const { data: scriptRaw, isLoading: scriptLoading } = useGetScriptTagStatus({
    query: { enabled: auth.isAuthenticated() },
  })
  const scriptStatus = (scriptRaw ?? null) as ScriptTagStatusResponse | null
  const scriptTagInstalled = scriptStatus?.installed ?? null

  // --- Orval Mutation 훅: 스크립트 설치/제거 ---
  const installMutation = useInstallScriptTag()
  const uninstallMutation = useUninstallScriptTag()

  const loading = shopLoading || scriptLoading

  // 인증 안 된 경우 리다이렉트
  useEffect(() => {
    if (!auth.isAuthenticated()) {
      navigate({ to: '/' })
    }
  }, [navigate])

  /** 스크립트 자동 설치 */
  const handleInstall = async () => {
    const ok = await confirm(
      '솔루션 스크립트를 자동 설치하시겠습니까?\n쇼핑몰 전체 페이지에 트래킹 스크립트가 추가됩니다.',
    )
    if (!ok) return

    try {
      await installMutation.mutateAsync()
      toast.success('스크립트가 설치되었어요.')
      // 스크립트 상태 + Shop 쿼리 무효화 → 상세페이지 + 사이드바 동시 반영
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetScriptTagStatusQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() }),
      ])
    } catch (error: any) {
      if (error.message) {
        toast.error(error.message)
      } else {
        toast.error('스크립트 설치에 실패했어요. 잠시 후 다시 시도해주세요.')
      }
    }
  }

  /** 스크립트 제거 */
  const handleUninstall = async () => {
    const ok = await confirm(
      '솔루션 스크립트를 제거하시겠습니까?\n트래킹 데이터 수집이 중단됩니다.',
    )
    if (!ok) return

    try {
      await uninstallMutation.mutateAsync()
      toast.success('스크립트가 제거되었어요.')
      // 스크립트 상태 + Shop 쿼리 무효화 → 상세페이지 + 사이드바 동시 반영
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetScriptTagStatusQueryKey() }),
        queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() }),
      ])
    } catch (error: any) {
      if (error.message) {
        toast.error(error.message)
      } else {
        toast.error('스크립트 제거에 실패했어요. 잠시 후 다시 시도해주세요.')
      }
    }
  }

  const actionLoading = installMutation.isPending || uninstallMutation.isPending

  if (loading || !shop) {
    return (
      <div className="flex items-center justify-center py-20 min-h-[60vh]">
        <div className="text-outline-variant text-sm">로딩 중...</div>
      </div>
    )
  }

  // 스크립트 설치는 다른 연동의 선행 조건 — 별도 선행 조건 없음.

  return (
    <>
      <SetupPage
        title="카페24 스크립트 설치"
        connected={!!scriptTagInstalled}
        connectedLabel="설치됨"
        disconnectedLabel="미설치"
      >
        <ScriptGuide
          scriptTagInstalled={scriptTagInstalled}
          actionLoading={actionLoading}
          onInstall={handleInstall}
          onUninstall={handleUninstall}
        />
      </SetupPage>
      <ConfirmDialog />
    </>
  )
}

/** 가이드 + 설치 버튼 통합 */
function ScriptGuide({
  scriptTagInstalled,
  actionLoading,
  onInstall,
  onUninstall,
}: {
  scriptTagInstalled: boolean | null
  actionLoading: boolean
  onInstall: () => void
  onUninstall: () => void
}) {
  return (
    <div className="space-y-2">
      <GuideStep number={1} title="스크립트란?">
        <p>
          쇼핑몰에 설치되는 데이터 수집 코드예요.
          방문자 분석(믹스패널)과 메타 전환 API에 필요해요.
        </p>
      </GuideStep>

      <GuideStep number={2} title="기존 수동 설치 코드 제거 (해당되는 경우)">
        <p>
          이전에 스크립트 코드를 직접 붙여넣으셨다면 데이터 수집은 정상적으로 동작하고 있어요.
          자동 설치로 전환하면 앱에서 설치 상태를 관리할 수 있어요.
        </p>
        <p className="mt-2">
          자동 설치 전에 기존 코드를 삭제해주세요:
        </p>
        <div className="my-3 p-3 bg-surface-low rounded-lg text-sm text-on-surface-variant space-y-1">
          <p className="font-medium text-on-surface">삭제 위치:</p>
          <p>카페24 관리자 &gt; 쇼핑몰 설정 &gt; 기본 설정 &gt; 검색 엔진 최적화(SEO) &gt; 고급설정 &gt; 코드 직접입력</p>
          <p>PC 쇼핑몰, 모바일 쇼핑몰 Head 영역에서 아래 코드를 삭제해주세요:</p>
        </div>
        <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs overflow-x-auto">
          <code>{`<!-- OneBPlusSchool 트래커 스크립트 -->\n<script src="https://tracker.1bpluschool.com/tracker.js" async></script>`}</code>
        </pre>
        <TipBox type="tip">
          삭제하지 않고 자동 설치를 먼저 진행해도 괜찮아요. 중복 실행 방지 처리가 되어 있어 동작에 문제없어요. 나중에 정리해주시면 돼요.
        </TipBox>
      </GuideStep>

      <GuideStep number={3} title="자동 설치">
        <p className="mb-3">
          버튼을 누르면 PC/모바일 쇼핑몰에 자동으로 설치돼요.
        </p>
        {scriptTagInstalled === null ? (
          <p className="text-sm text-outline-variant">설치 상태 확인 중...</p>
        ) : scriptTagInstalled ? (
          <div className="space-y-3">
            <TipBox type="tip">
              스크립트가 설치되어 있어요. 데이터 수집이 진행 중이에요.
            </TipBox>
            <Button
              variant="danger-outline"
              onClick={onUninstall}
              loading={actionLoading}
              size="sm"
            >
              {actionLoading ? '제거 중...' : '스크립트 제거'}
            </Button>
          </div>
        ) : (
          <Button
            variant="primary"
            onClick={onInstall}
            disabled={actionLoading}
            loading={actionLoading}
          >
            {actionLoading ? '설치 중...' : '설치하기'}
          </Button>
        )}
      </GuideStep>

    </div>
  )
}
